/**
 * Composition Engine — the concrete spatial intentions of the direction.
 *
 * Turns style/mood/focus/flow into the decisions an art director would
 * sketch on a napkin: how much canvas the hero owns, whether the heading
 * overlaps it, where the CTA lands, whether the offer breaks the grid,
 * how tight the footer is. All values are intent (ratios and booleans),
 * not geometry — layout engines downstream own the pixels.
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Build composition decisions.
 * Returns {
 *   heroAreaRatio, heroImportance, headingOverlapsHero, ctaPlacement,
 *   ctaImportance, offerEmphasis, offerBreaksGrid, footerCompressed,
 *   layerDepth, objectOverlap, notes[]
 * }
 */
export const composeIntent = ({ audience, mood, style, focus, flow, trends, rng }) => {
  const notes = [];

  // Hero sizing: image-first audiences get a dominant hero; a hero focal
  // point pushes it further; sophistication trims it back for restraint.
  let heroAreaRatio = audience.heroImageImportance === 'high' ? 0.42 : 0.32;
  if (focus.primary === 'hero') heroAreaRatio += 0.08;
  if (mood.sophistication >= 0.8) heroAreaRatio -= 0.04;
  heroAreaRatio = Number(clamp(heroAreaRatio, 0.25, 0.55).toFixed(2));
  notes.push(`Hero occupies ~${Math.round(heroAreaRatio * 100)}% of the canvas.`);

  // Overlap is a depth move — only styles that carry depth earn it.
  const objectOverlap = style.layerDepth >= 0.5 || trends.some((t) => t.effects.objectOverlap);
  const headingOverlapsHero = objectOverlap && focus.primary !== 'primaryHeading' && rng() < 0.6;
  if (headingOverlapsHero) notes.push('Heading overlaps the hero for depth.');

  // CTA lands at the flow's visual endpoint. Its importance rises with
  // energy — calm briefs whisper, urgent briefs point.
  const ctaImportance = Number(clamp(0.35 + mood.energy * 0.5, 0.35, 0.85).toFixed(2));
  notes.push(`CTA placed at the ${flow.endpoint} — the flow's natural endpoint.`);

  // The offer may break the grid only when it is the focal point (an
  // intentional rule violation reads premium; an accidental one reads broken).
  const offerBreaksGrid = focus.primary === 'offer' && rng() < 0.7;
  if (offerBreaksGrid) notes.push('Offer badge breaks the grid intentionally.');

  const footerCompressed = style.whitespaceBias >= 0.6;
  if (footerCompressed) notes.push('Footer compressed to protect negative space.');

  return {
    heroAreaRatio,
    heroImportance: audience.heroImageImportance,
    headingOverlapsHero,
    ctaPlacement: flow.endpoint,
    ctaImportance,
    offerEmphasis: focus.primary === 'offer' ? 'dominant' : mood.energy >= 0.6 ? 'strong' : 'supporting',
    offerBreaksGrid,
    footerCompressed,
    layerDepth: style.layerDepth,
    objectOverlap,
    notes,
  };
};
